import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useDeployedAppBuildId } from "@/api/app-update/useDeployedAppBuildId";
import { toast } from "@/components/ui/sonner";
import {
  APP_UPDATE_TOAST_ID,
  VITE_PRELOAD_ERROR_EVENT,
} from "@/constants/app-update";
import {
  readDismissedAppBuildId,
  writeDismissedAppBuildId,
} from "@/lib/app-update/dismissed-app-build-id";

const reloadPage = (): void => {
  window.location.reload();
};

// NOTE: NSAI-8170 — a tab keeps running the bundle it loaded until the user
// reloads it, so after a deploy it renders new server features with old code
// (NSAI-6892) and, once the old chunks are gone, fails to open lazy routes.
// This offers the reload: a persistent "New version available" toast with a
// Refresh action, shown while the deployed build differs from this tab's.
//
// Closing the toast parks that build id for the session; a later build asks
// again. Nothing reloads on its own — an unsent message or an upload in flight
// is the user's to finish first.
export const useAppUpdatePrompt = (): void => {
  const { t } = useTranslation("common");
  const { data: deployedBuildId, refetch } = useDeployedAppBuildId();
  const [dismissedBuildId, setDismissedBuildId] = useState(
    readDismissedAppBuildId,
  );

  // NOTE: A lazy chunk that fails to load is the strongest hint that the
  // server moved on, so it asks at once instead of waiting for the next poll.
  // It only asks: a network blip fails a chunk too, and must not claim a new
  // version, nor reopen a toast closed for the build that is still deployed.
  useEffect(() => {
    const handlePreloadError = (): void => {
      void refetch();
    };

    window.addEventListener(VITE_PRELOAD_ERROR_EVENT, handlePreloadError);

    return (): void => {
      window.removeEventListener(VITE_PRELOAD_ERROR_EVENT, handlePreloadError);
    };
  }, [refetch]);

  const isUpdateAvailable =
    Boolean(deployedBuildId) &&
    deployedBuildId !== __APP_BUILD_ID__ &&
    deployedBuildId !== dismissedBuildId;

  const handleDismiss = useCallback((): void => {
    if (!deployedBuildId) {
      return;
    }

    writeDismissedAppBuildId({ buildId: deployedBuildId });
    setDismissedBuildId(deployedBuildId);
  }, [deployedBuildId]);

  useEffect(() => {
    // NOTE: Also covers a rollback to this tab's own build while the toast is
    // up — there is nothing newer to refresh to any more.
    if (!isUpdateAvailable) {
      toast.dismiss(APP_UPDATE_TOAST_ID);
      return;
    }

    toast.info(t("app_update.title"), {
      id: APP_UPDATE_TOAST_ID,
      description: t("app_update.description"),
      duration: Number.POSITIVE_INFINITY,
      action: { label: t("app_update.refresh"), onClick: reloadPage },
      onDismiss: handleDismiss,
    });
  }, [handleDismiss, isUpdateAvailable, t]);
};
